import { ReactNode, createContext, useEffect, useState } from "react";
// Built-in brand mark — the interlocked-hearts glyph, shipped in the bundle so
// every tenant renders SOMETHING even with no `brandMarkSrc` in its config.
// The svg rule in client/config/webpack.config.js chains @svgr/webpack onto
// file-loader, so the default import is the emitted asset URL (the named
// `ReactComponent` export is the inline component) — a URL is what this
// module needs, because a per-tenant override is a path, not a module.
import defaultBrandMark from "../../img/logo.svg";

export type TrustBadgeKind = "BOARD_CERTIFIED" | "ACCREDITED" | "GOOGLE_RATING";

export type TrustBadge = {
  kind: TrustBadgeKind;
  label: string;
};

// Mirrors the server FinancingApplication / Payment provider enums.
export type FinancingProviderKind = "CHERRY" | "CARECREDIT" | "PATIENTFI";

// A patient-financing partner surfaced on the quote summary. Per-tenant so each
// clinic points the prequalify CTA at its own merchant link without code changes.
export type FinancingProvider = {
  provider: FinancingProviderKind;
  // CTA label; overridable via i18n at the call site.
  label: string;
  // Deep-linked prequalify / application URL (opened in a new tab).
  applyUrl: string;
  // True only when the provider's prequalify is a genuine SOFT credit pull.
  // Gates the "won't affect your credit score" reassurance line — never render
  // that claim for a provider that does a hard pull.
  softPull?: boolean;
};

/**
 * Outbound destinations the tenant wants a viewer sent to — the /live end
 * screen's tile row and its "Visit topaesthetics.com" line (Group D · D5).
 *
 * EVERY ENTRY OPTIONAL, and an absent one renders NOTHING: a clinic that has
 * no gallery must end up with one fewer tile, never a tile that goes nowhere.
 * Absolute URLs, because these leave the SPA.
 */
export type AppLinks = {
  quote?: string;
  book?: string;
  evaluation?: string;
  gallery?: string;
  site?: string;
  /** The coordinator's line, in E.164 — the ONE destination here that is a
   *  number rather than a URL, because what a page does with it is build a
   *  `tel:` link (the replay page's "Call Ashley"). Same absence rule as the
   *  rest: no number, no call button — never a hardcoded one. */
  phone?: string;
};

/**
 * The tenant's social profiles, for the end screen's "Connect with us on
 * social media!" row. Same rule as AppLinks: a platform with no URL is simply
 * not rendered — no dead circles, and no handles invented on a clinic's
 * behalf.
 */
export type AppSocials = {
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  facebook?: string;
};

type AppData = {
  logo: string;
  preview: string;
  appBackgroundVideo: string;
  appBackground: string;
  superTitle: string;
  title: string;
  description: string;
  // Trust signals rendered next to the booking CTA. Per-tenant — leave
  // empty or omit for tenants without defensible claims.
  trustBand?: TrustBadge[];
  // Display name for the SMS auto-reply agent — used in the patient
  // SMS tab attribution label and the [AI Agent] / [My SMS] toggle.
  // Defaults to "Ashley" when omitted. Per-tenant so a new clinic's
  // bundle can pick its own agent persona without code changes.
  smsAgentDisplayName?: string;
  // Patient-financing partners for the quote-summary prequalify CTA.
  // [0] is the primary (drives the financing-forward variant's hero CTA);
  // remaining entries are fallbacks. Omit/empty → the variant degrades to the
  // read-only CareCredit financing modal so there's never a dead CTA.
  financingProviders?: FinancingProvider[];
  // Funnel price-display cap (USD). When a quoted figure exceeds this, the
  // quote summary shows "$<cap>+" instead of an exact number, and — when the
  // headline floor itself is over the cap — routes the patient to a
  // coordinator for a final quote. Defaults to 15000 when omitted. Per-tenant
  // so each clinic can tune its own ceiling. Never applies to the fixed
  // booking deposit.
  quoteDisplayCap?: number;
  // Native iOS app the signed-in web patient portal is being sunset in favor
  // of. When present, the my-account / my-encounters landing dashboards
  // render InstallAppPrompt instead of their normal content (see
  // patient_my_account_dashboard.tsx / patient_dashboard.tsx). Omit for a
  // tenant without a shipped app — behavior is unchanged (no prompt).
  iosApp?: {
    appStoreUrl: string;
  };
  // E.164 phone number for the clinic's patient-facing SMS line — the
  // number an `sms:` deep link opens (e.g. BookDirectPage.tsx's
  // EXPIRED-link "Message us" CTA). Per-tenant — never hardcode a phone
  // number in a component; read it from here and hide the affected CTA
  // when unset.
  patientSmsNumber?: string;
  // The tenant's brand MARK — the small square-ish glyph (not the wordmark in
  // `logo`) rendered at avatar size: the /live viewer's sticky header and the
  // chat title-row tile. Same convention as `logo`: a path relative to the
  // public/ root ("img/mark.svg"), or an absolute http(s)/data URL. OMIT IT to
  // get the bundled interlocked-hearts default — which is why the shipped
  // top-aesthetics config does not set it. Read the RESOLVED value off the
  // context (`brandMarkSrc` below), never this raw field.
  brandMarkSrc?: string;
  // Where the /live end screen's tiles and site link point, and the tenant's
  // social profiles beside them (Group D · D5). Both blocks and every entry
  // inside them are optional — a tenant that ships neither renders neither,
  // which is why nothing on those screens has a hardcoded fallback URL.
  links?: AppLinks;
  socials?: AppSocials;
  // The /live share invitation, per tenant (D11). When set it is used
  // VERBATIM on both non-live share buttons — the end screen and the
  // scheduled page — in preference to the date-derived sentence, because it
  // is what the clinic wants said. Omit it and the sentence falls back to the
  // next scheduled broadcast date. Editing it from the Stream Overview is a
  // later group (L) and needs a settings model; this is the key it will write.
  livestream?: {
    share?: {
      nextLiveInvite?: string;
    };
    /** The host as the viewer sheet names him, e.g. "Dr. Palladino"; the
     *  scheduled page prints "with {hostName}" in its date line (SC1). */
    hostName?: string;
    /** The host's full name for the v2 pages' host strip and host row,
     *  e.g. "Dr. Humberto Palladino". Omit it and those rows drop the name. */
    hostFullName?: string;
    /** The line under that name, e.g. "Plastic surgeon at Top Aesthetics".
     *  Omit it and the line is dropped. */
    hostRole?: string;
    /** The host's headshot, a public/-relative path or an absolute URL,
     *  resolved with `resolvePublicAsset`. Omit it and the rows go without a
     *  photo rather than show a broken image. */
    hostPhoto?: string;
    /** The v2 waiting room's side photo (and phone band), 1600px wide. Omit
     *  it and the page drops the photo panel and centres the content
     *  column. */
    waitingPhoto?: string;
    /** An 800px-wide copy of `waitingPhoto`, offered beside it in `srcset`
     *  so a phone's band need not download the 1600px file. Optional; used
     *  only together with `waitingPhoto`. */
    waitingPhotoSmall?: string;
    /** The v2 end screen's side photo, same contract as `waitingPhoto`. */
    endPhoto?: string;
    /** The 800px copy of `endPhoto`, same contract as `waitingPhotoSmall`. */
    endPhotoSmall?: string;
    /** The v2 end screen's caption under the free-evaluation path, the
     *  clinic's own workflow in its words. "{host}" is replaced with
     *  `hostName` ("our surgeon" without one). Omit it for the default, "No
     *  surgical plan from us yet? Send photos and {host} reviews them." */
    evaluationCaption?: string;
    /** The caption under the scheduling path, same contract. Omit it for
     *  the default, "Already have your plan and paid your deposit? Pick
     *  your date." */
    quoteCaption?: string;
    /** Shown on the between-streams page only when nothing is scheduled
     *  (owner answer Q3): "Usually Mondays around 5 PM ET". Omit to hide. */
    cadenceLine?: string;
  };
};

// Types for the context value
interface AppConfigContextType {
  isLoading: boolean;
  data: AppData | null;
  // Ready-to-render URL for the tenant brand mark — always a usable src, even
  // while the config is still loading or failed to load. Resolved once here so
  // no consumer has to re-implement the PUBLIC_URL join or the default.
  brandMarkSrc: string;
}

// Create a context with the initial value and type assertion
export const AppConfigContext = createContext<AppConfigContextType | null>(
  null,
);

interface AppConfigProviderProps {
  children: ReactNode;
}

// A config image path → something an <img src> can use. An absolute URL (or a
// data: URI) is passed through untouched; anything else is treated as a
// public/-relative path, exactly like `logo` (see DashboardContainer.tsx).
// Nothing configured (missing or blank) → undefined, so the caller decides
// what an absent image means: the brand mark below falls back to the bundled
// default, and the /live v2 pages' photos simply leave the image out.
export function resolvePublicAsset(
  path: string | undefined,
): string | undefined {
  const configured = path?.trim();
  if (!configured) return undefined;
  if (/^(https?:)?\/\//.test(configured) || configured.startsWith("data:")) {
    return configured;
  }
  return `${process.env.PUBLIC_URL}/${configured.replace(/^\//, "")}`;
}

// `brandMarkSrc` from the config → an <img src>, by the rule above, with the
// bundled default mark when nothing is configured.
export function resolveBrandMarkSrc(data: AppData | null): string {
  return resolvePublicAsset(data?.brandMarkSrc) ?? defaultBrandMark;
}

const FALLBACK_APP_DATA: AppData = {
  logo: "",
  preview: "",
  appBackgroundVideo: "",
  appBackground: "",
  superTitle: "",
  title: "",
  description: "",
  trustBand: [],
  smsAgentDisplayName: "Ashley",
  financingProviders: [],
};

// Provider component
export const AppConfigProvider = ({ children }: AppConfigProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);

  const [data, setData] = useState<AppData | null>(null);

  useEffect(() => {
    const fetchWithRetry = async (retries = 2): Promise<void> => {
      for (let i = 0; i <= retries; i++) {
        try {
          const response = await fetch(
            `${process.env.PUBLIC_URL}/${process.env.REACT_APP_CONFIG}`,
          );
          const data = await response.json();
          document.title = data?.title || "";
          setData(data);
          return;
        } catch (error) {
          if (i === retries) {
            console.error("Error fetching data:", error);
            // Render with safe defaults so the app doesn't hang on a blank screen
            setData(FALLBACK_APP_DATA);
            return;
          }
          await new Promise((r) => setTimeout(r, 500));
        }
      }
    };
    fetchWithRetry().finally(() => setIsLoading(false));
  }, []); // Empty dependency array to run only once

  return (
    <AppConfigContext.Provider
      value={{ isLoading, data, brandMarkSrc: resolveBrandMarkSrc(data) }}
    >
      {children}
    </AppConfigContext.Provider>
  );
};
